'use client';

import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface ListingFrame {
	src: string;
	alt: string;
}

interface ListingCarouselProps {
	frames: ListingFrame[];
	/** Intrinsic size of every frame (they all share the listing's aspect ratio). */
	frameWidth: number;
	frameHeight: number;
}

const GAP_PX = 16;

/**
 * The App Store listing frames as a horizontal carousel that lives inside the
 * page gutter. Native scroll with snap points does the work; the arrows just
 * scroll by one frame for mouse and keyboard users.
 */
export function ListingCarousel({ frames, frameWidth, frameHeight }: ListingCarouselProps) {
	const trackRef = useRef<HTMLUListElement>(null);
	const [atStart, setAtStart] = useState(true);
	const [atEnd, setAtEnd] = useState(false);

	const updateEdges = useCallback(() => {
		const track = trackRef.current;
		if (!track) return;
		setAtStart(track.scrollLeft <= 1);
		setAtEnd(track.scrollLeft + track.clientWidth >= track.scrollWidth - 1);
	}, []);

	useEffect(() => {
		updateEdges();
		const track = trackRef.current;
		if (!track) return;
		const observer = new ResizeObserver(updateEdges);
		observer.observe(track);
		return () => observer.disconnect();
	}, [updateEdges]);

	const scrollByFrame = (direction: 1 | -1) => {
		const track = trackRef.current;
		if (!track) return;
		const first = track.firstElementChild as HTMLElement | null;
		const step = (first?.offsetWidth ?? 280) + GAP_PX;
		track.scrollBy({ left: step * direction, behavior: 'smooth' });
	};

	const arrow =
		'inline-flex h-11 w-11 items-center justify-center rounded-full border border-neutral-950/20 bg-white text-neutral-950 transition-colors hover:border-neutral-950 disabled:cursor-default disabled:opacity-30 disabled:hover:border-neutral-950/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-950';

	return (
		<div>
			<ul
				ref={trackRef}
				onScroll={updateEdges}
				className="scrollbar-hide -mx-1 flex snap-x snap-mandatory gap-4 overflow-x-auto px-1 pb-2"
				aria-label="Screens from the Workman app"
			>
				{frames.map((frame) => (
					<li
						key={frame.src}
						className="w-[240px] shrink-0 snap-start overflow-hidden rounded-[24px] border-[1.5px] border-neutral-300 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.06)] sm:w-[280px]"
					>
						<Image
							src={frame.src}
							alt={frame.alt}
							width={frameWidth}
							height={frameHeight}
							sizes="(min-width: 640px) 280px, 240px"
							draggable={false}
							className="pointer-events-none block h-auto w-full select-none"
						/>
					</li>
				))}
			</ul>

			<div className="mt-5 flex items-center gap-2">
				<button
					type="button"
					onClick={() => scrollByFrame(-1)}
					disabled={atStart}
					aria-label="Previous screen"
					className={arrow}
				>
					<svg
						viewBox="0 0 24 24"
						aria-hidden="true"
						className="h-5 w-5"
						fill="none"
						stroke="currentColor"
						strokeWidth="2.2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="m15 18-6-6 6-6" />
					</svg>
				</button>
				<button
					type="button"
					onClick={() => scrollByFrame(1)}
					disabled={atEnd}
					aria-label="Next screen"
					className={arrow}
				>
					<svg
						viewBox="0 0 24 24"
						aria-hidden="true"
						className="h-5 w-5"
						fill="none"
						stroke="currentColor"
						strokeWidth="2.2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="m9 18 6-6-6-6" />
					</svg>
				</button>
			</div>
		</div>
	);
}
